/**
 * TransitionFrameworkTestScene — dev harness for AGE-1421.
 *
 * Boots a SceneRouter wired to `PaintedSceneTransitionRunner` and uses
 * `PathHomeScene` (Scene 1) as the source. Hotkeys fire each of the three
 * transition kinds against `HearthScene` (Scene 2):
 *
 *   1 / E — edge-crossfade
 *   2 / D — doorway
 *   3 / C — camera-pan
 *   R     — reset (return to PathHomeScene fresh)
 *
 * Reached via the dev query-string flag `?scene=transitionTest`. BootScene
 * detects the flag and short-circuits straight here, bypassing the title
 * flow. The harness is a thin shell — it owns the router and a debug HUD
 * but the actual painted-scene rendering and walker movement are
 * delegated to `PathHomeScene` / `HearthScene`.
 *
 * Acceptance for AGE-1421:
 *   - All three transitions work between Scene 1 ↔ Scene 2.
 *   - Walker position is visually continuous through edge-crossfade
 *     (the shared spawn point on `south-trail` aligns the screen-space
 *     position of the walker between scenes).
 *   - 60fps target during transition (no frame drops introduced by the
 *     runner; profile via the browser's Performance tab if regressions
 *     show up).
 *   - Audio crossfade is smooth (no clicks/pops) — the equal-power
 *     `MusicCrossfader` covers this even with no music keys loaded
 *     (silence has no clicks).
 */

import * as Phaser from 'phaser';
import {
  PaintedSceneTransitionRunner,
  TRANSITION_OVERLAY_SCENE_KEY,
} from './SceneTransition';
import {
  HEARTH_SCENE_PHASER_KEY,
  HEARTH_SCENE_MANIFEST_KEY,
} from './HearthScene';
import {
  PATH_HOME_SCENE_PHASER_KEY,
  PATH_HOME_SCENE_MANIFEST_KEY,
} from './PathHomeScene';
import {
  SceneRouter,
  type SceneRouterOptions,
} from './SceneRouter';
import {
  validateJourneyManifest,
  type JourneyManifest,
  type TransitionKind,
} from './journeyManifest.schema';

export const TRANSITION_FRAMEWORK_TEST_SCENE_KEY = 'TransitionFrameworkTestScene' as const;

const FONT_HEAD = "'Almendra', serif";
const COLOR_INK = '#eae0c8';
const COLOR_INK_DIM = '#9a907a';

/**
 * Inline fallback manifest used when `journey-manifest.json` isn't in the
 * cache. Mirrors `PathHomeScene`'s inline manifest with both scenes so the
 * router can validate the round-trip targets.
 */
const FRAMEWORK_TEST_INLINE_MANIFEST: JourneyManifest = {
  version: 1,
  initialScene: PATH_HOME_SCENE_MANIFEST_KEY,
  scenes: [
    {
      key: PATH_HOME_SCENE_MANIFEST_KEY,
      displayName: 'The Path Home',
      music: 'music-dawn-path',
      assetPath: 'assets/scenes/scene-1-path-home/spec.json',
      defaultSpawn: { x: 0.5, y: 0.78 },
      edgeZones: [],
      interactions: [],
      hiddenZones: [],
      specialMechanics: [],
      materialZones: [],
    },
    {
      key: HEARTH_SCENE_MANIFEST_KEY,
      displayName: 'The Hearth',
      music: 'music-hearth-warmth',
      assetPath: 'assets/scenes/scene-2-hearth/spec.json',
      defaultSpawn: { x: 0.5, y: 0.82 },
      edgeZones: [],
      interactions: [],
      hiddenZones: [],
      specialMechanics: [],
      materialZones: [],
    },
  ],
};

const MANIFEST_TO_PHASER_KEY = new Map<string, string>([
  [PATH_HOME_SCENE_MANIFEST_KEY, PATH_HOME_SCENE_PHASER_KEY],
  [HEARTH_SCENE_MANIFEST_KEY, HEARTH_SCENE_PHASER_KEY],
]);

export class TransitionFrameworkTestScene extends Phaser.Scene {
  public static readonly KEY = TRANSITION_FRAMEWORK_TEST_SCENE_KEY;

  private router!: SceneRouter;
  private runner!: PaintedSceneTransitionRunner;
  private hud!: Phaser.GameObjects.Text;
  private status!: Phaser.GameObjects.Text;

  constructor() {
    super({ key: TRANSITION_FRAMEWORK_TEST_SCENE_KEY });
  }

  preload(): void {
    if (!this.cache.json.has('journey-manifest')) {
      this.load.json('journey-manifest', 'assets/scenes/journey-manifest.json');
    }
  }

  create(): void {
    const manifest = this.resolveManifest();
    this.runner = new PaintedSceneTransitionRunner(this.game, {
      manifestToPhaserKey: MANIFEST_TO_PHASER_KEY,
    });

    const opts: SceneRouterOptions = {
      manifest,
      transition: this.runner,
      // In-memory storage — the framework test isn't persistence-driven.
      storage: {
        getItem: () => null,
        setItem: () => undefined,
        removeItem: () => undefined,
      },
    };
    this.router = new SceneRouter(opts);

    this.buildHud();
    this.bindHotkeys();

    // Ensure the overlay scene is registered. The runner launches it on
    // demand, but Phaser only knows about scenes that were added at boot.
    if (!this.scene.get(TRANSITION_OVERLAY_SCENE_KEY)) {
      console.warn(
        `[TransitionFrameworkTest] overlay scene "${TRANSITION_OVERLAY_SCENE_KEY}" not registered — vignette/doorway will degrade`,
      );
    }

    // Boot Scene 1 in parallel so the harness HUD overlays it. The router
    // is at `currentScene = scene-1-path-home` after construction.
    this.scene.launch(PATH_HOME_SCENE_PHASER_KEY, {
      spawn: { x: 0.5, y: 0.78 },
      fromScene: TRANSITION_FRAMEWORK_TEST_SCENE_KEY,
    });
    this.scene.bringToTop(this);
  }

  private resolveManifest(): JourneyManifest {
    const raw = this.cache.json.get('journey-manifest') as unknown;
    if (raw) {
      try {
        return validateJourneyManifest(raw);
      } catch (err) {
        console.warn(
          `[TransitionFrameworkTest] journey-manifest invalid; falling back to inline (${(err as Error).message})`,
        );
      }
    }
    return FRAMEWORK_TEST_INLINE_MANIFEST;
  }

  private buildHud(): void {
    const w = this.scale.width;
    const h = this.scale.height;
    const bar = this.add
      .rectangle(0, 0, w, 56, 0x000000, 0.55)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(2000);
    void bar;

    this.hud = this.add
      .text(8, 6, 'Transition Framework Test (AGE-1421)', {
        fontFamily: FONT_HEAD,
        fontSize: '16px',
        color: COLOR_INK,
      })
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(2001);

    this.status = this.add
      .text(
        8,
        28,
        '1 = edge-crossfade   2 = doorway   3 = camera-pan   4 = threshold-zoom   R = reset',
        {
          fontFamily: FONT_HEAD,
          fontSize: '12px',
          color: COLOR_INK_DIM,
        },
      )
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(2001);
  }

  private bindHotkeys(): void {
    const kb = this.input.keyboard;
    if (!kb) return;
    kb.on(Phaser.Input.Keyboard.Events.ANY_KEY_DOWN, (event: KeyboardEvent) => {
      switch (event.code) {
        case 'Digit1':
        case 'KeyE':
          this.fireTransition('edge-crossfade');
          break;
        case 'Digit2':
        case 'KeyD':
          this.fireTransition('doorway');
          break;
        case 'Digit3':
        case 'KeyC':
          this.fireTransition('camera-pan');
          break;
        case 'Digit4':
        case 'KeyT':
          this.fireTransition('threshold-zoom');
          break;
        case 'KeyR':
          this.resetToScene1();
          break;
      }
    });
  }

  /**
   * Drive a transition via the router. Picks the target as "the other
   * scene" so each hotkey toggles between Scene 1 and Scene 2.
   */
  private fireTransition(kind: TransitionKind): void {
    if (this.runner.isBusy()) {
      this.flashStatus('transition in flight — wait for settle', '#ff8888');
      return;
    }
    const current = this.router.currentSceneKey();
    const target =
      current === PATH_HOME_SCENE_MANIFEST_KEY
        ? HEARTH_SCENE_MANIFEST_KEY
        : PATH_HOME_SCENE_MANIFEST_KEY;
    this.flashStatus(`▶ ${kind}: ${current} → ${target}`, COLOR_INK);
    void this.router
      .goTo(target, { transitionKind: kind })
      .then(() => this.flashStatus(`✓ ${kind} settled on ${target}`, '#88ff88'))
      .catch((err) => this.flashStatus(`✗ ${kind} failed: ${(err as Error).message}`, '#ff8888'));
  }

  private resetToScene1(): void {
    if (this.runner.isBusy()) return;
    const current = this.router.currentSceneKey();
    if (current === PATH_HOME_SCENE_MANIFEST_KEY) {
      this.flashStatus('already on scene-1-path-home', COLOR_INK_DIM);
      return;
    }
    void this.router.goTo(PATH_HOME_SCENE_MANIFEST_KEY, {
      transitionKind: 'edge-crossfade',
    });
  }

  private flashStatus(msg: string, color: string): void {
    this.status.setText(msg).setColor(color);
  }
}
